import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import PageSEO from "@/components/PageSEO";
import { breadcrumb } from "@/lib/schemas";
import ScheduleNav from "@/components/ScheduleNav";
import ChoosePathModal from "@/components/ChoosePathModal";
import { Button } from "@/components/ui/button";
import { CheckCircle, Shield, Sparkles, BookOpen, Wrench, ArrowLeft, Award } from "lucide-react";
import anishaPerry from "@/assets/anisha-perry.png";

const Day2 = () => {
  const [modalOpen, setModalOpen] = useState(false);

  const youWillLearn = [
    "Which AI tools are worth using in real estate",
    "Which tools waste time or money",
    "How to avoid Fair Housing issues",
    "How to protect yourself and your brokerage",
    "What you should never generate or automate as an agent",
  ];

  const youWillBuild = [
    { text: "Custom ChatGPT GPTs (your own assistants)", icon: Sparkles },
    { text: "Custom Gemini Gems (Google's AI Gems)", icon: Sparkles },
  ];

  const walkAwayWith = [
    "Pre-Foreclosure Outreach Advisor",
    "ADU Pricing + ROI Assistant",
    "Listing Description + Marketing Copy Generator",
    "Buyer Follow-Up + Objection Handler",
    "Open House Script Builder",
    "Newsletter + Database Nurture Gem",
    "Client Communication Assistant",
  ];

  const bonuses = [
    "A Real Estate AI Compliance Checklist",
    "My best AI tools under $50/month",
    "Clear guardrails for responsible use",
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#fffcf2] font-sans">
      <PageSEO
        title="Day 2 · AI Tools & Compliance | Tech Founder Summit"
        description="Day 2 of the Tech Founder Summit. Build custom GPTs and Gemini Gems for real estate, with Fair Housing and brokerage compliance guardrails."
        canonical="https://marquettabryant.com/day-2"
        jsonLd={[breadcrumb([{name:"Home",path:"/"},{name:"Tech Founder Summit",path:"/tech-founder-summit"},{name:"Day 2",path:"/day-2"}])]}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#2e4053] to-[#1a1a2e] pt-8 pb-16 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-64 h-64 bg-[#1e90ff]/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-20 w-80 h-80 bg-[#d8239b]/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          {/* Back Link */}
          <a 
            href="https://marquettabryant.com/tech-founder-summit" 
            className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            ← Back to Summit Home
          </a>
          
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1e90ff]/20 rounded-full mb-6">
              <Shield className="w-5 h-5 text-[#1e90ff]" />
              <span className="text-[#1e90ff] font-semibold text-sm">DAY 2 • FEB 11</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              AI Tools + Compliance + Custom GPTs & Gems
            </h1>
            
            <p className="text-xl md:text-2xl text-white/80 mb-10 max-w-2xl mx-auto">
              Tools you can actually use, without risking compliance.
            </p>
            
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-[#d8239b] to-[#ff69b4] hover:from-[#d8239b]/90 hover:to-[#ff69b4]/90 text-white font-bold px-10 py-7 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
              onClick={() => setModalOpen(true)}
            >
              Choose Your Path
            </Button>
          </div>
        </div>
      </section>
      
      <ScheduleNav />
      
      <main className="pt-12 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {/* Outcome & Tagline Card */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 md:p-10 mb-8">
              {/* Outcome Badge */}
              <div className="bg-gradient-to-r from-[#d8239b]/10 to-[#ff69b4]/10 rounded-2xl p-6 mb-6 border border-[#d8239b]/20">
                <div className="flex items-center gap-3 mb-2">
                  <span className="px-3 py-1 bg-[#d8239b] text-white text-xs font-bold rounded-full">OUTCOME</span>
                </div>
                <p className="text-lg md:text-xl text-[#2e4053] font-medium">
                  Learn what AI tools work in real estate, what does not, how to stay compliant, learn to build your own custom GPTs and Gemini Gems, plus leave with a secure list of mine.
                </p>
              </div>

              {/* Tagline */}
              <div className="text-center">
                <p className="text-2xl text-[#d8239b] font-bold italic">
                  No hype. No fluff. Tools with guardrails.
                </p>
              </div>
            </div>

            {/* You Will Learn Card */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 md:p-10 mb-8">
              <h3 className="text-xl font-bold text-[#2e4053] mb-6 flex items-center gap-3">
                <BookOpen className="w-6 h-6 text-[#1e90ff]" />
                You Will Learn
              </h3>
              <div className="space-y-3">
                {youWillLearn.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-[#fffcf2] rounded-xl">
                    <CheckCircle className="w-5 h-5 text-[#d8239b] flex-shrink-0 mt-0.5" />
                    <span className="text-[#2e4053]/80">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* You Will Build Card */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 md:p-10 mb-8">
              <h3 className="text-xl font-bold text-[#2e4053] mb-6 flex items-center gap-3">
                <Wrench className="w-6 h-6 text-[#1e90ff]" />
                You Will Build
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {youWillBuild.map((item, index) => (
                  <div key={index} className="flex items-center gap-4 p-4 bg-gradient-to-r from-[#1e90ff]/10 to-[#1e90ff]/5 rounded-xl border border-[#1e90ff]/20">
                    <div className="w-10 h-10 rounded-lg bg-[#1e90ff] flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-[#2e4053] font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Walk Away With Card */}
            <div className="bg-[#fffcf2] rounded-3xl shadow-lg border border-[#d8239b]/10 p-8 mb-8">
              <h3 className="font-bold text-[#2e4053] mb-4 text-lg">You Will Walk Away With</h3>
              <p className="text-[#2e4053]/70 mb-6">A secure list of my custom GPTs and Gemini Gems, with examples like:</p>
              <div className="grid md:grid-cols-2 gap-3 mb-8">
                {walkAwayWith.map((item, index) => (
                  <div key={index} className="flex items-start gap-2 text-[#2e4053]/80 bg-white p-3 rounded-lg border border-gray-100">
                    <span className="text-[#d8239b] font-bold">•</span>
                    {item}
                  </div>
                ))}
              </div>
              <div className="bg-white rounded-2xl p-6 border border-[#d8239b]/20">
                <p className="font-bold text-[#2e4053] mb-4">Plus:</p>
                <div className="space-y-3">
                  {bonuses.map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-r from-[#d8239b] to-[#ff69b4] flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-[#2e4053]/80">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Guest Compliance Instructor Card */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 md:p-10 mb-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#1e90ff] to-[#1e90ff]/70 flex items-center justify-center">
                  <Award className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#2e4053]">Guest Compliance Instructor</h3>
              </div>
              
              <div className="flex flex-col md:flex-row gap-6 items-start">
                {/* Photo */}
                <div className="flex-shrink-0">
                  <div className="w-32 h-32 rounded-2xl overflow-hidden shadow-lg ring-4 ring-[#1e90ff]/20">
                    <img 
                      src={anishaPerry} 
                      alt="Anisha Perry" 
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                
                {/* Bio */}
                <div className="flex-1">
                  <h4 className="text-lg font-bold text-[#2e4053] mb-1">Anisha Perry</h4>
                  <p className="text-sm text-[#1e90ff] font-medium mb-4">
                    Licensed Associate Broker | Licensed Real Estate Instructor | Retired U.S. Navy Veteran
                  </p>
                  <div className="space-y-3 text-[#2e4053]/80 text-sm leading-relaxed">
                    <p>
                      Anisha Perry is a licensed associate broker and a licensed real estate instructor in the state of Georgia, approved to teach real estate education, including pre-licensing coursework. As a former Georgia Real Estate Commission (GREC) employee, she is deeply familiar with the most common ways agents get into trouble, especially when it comes to marketing and advertising compliance.
                    </p>
                    <p>
                      On Day 2, Anisha will teach the <span className="font-semibold text-[#d8239b]">compliance side of AI</span>, helping you put clear guardrails around your AI use so you can move faster, stay protected, and avoid costly mistakes for yourself or your brokerage.
                    </p>
                    <p>
                      She is also a retired Navy veteran, a wife, and the mother of five daughters, and she understands firsthand how the right systems can help agents juggle business and life without burning out.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center mb-6">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-[#d8239b] to-[#ff69b4] hover:from-[#d8239b]/90 hover:to-[#ff69b4]/90 text-white font-bold px-12 py-7 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
                onClick={() => setModalOpen(true)}
              >
                Choose Your Path
              </Button>
            </div>

            {/* Disclaimer */}
            <p className="text-center text-sm text-[#2e4053]/50 italic mb-10">
              Educational content only. Not legal advice.
            </p>

            {/* Bottom Back Link */}
            <div className="text-center">
              <a 
                href="https://marquettabryant.com/tech-founder-summit" 
                className="inline-flex items-center gap-2 text-[#2e4053]/70 hover:text-[#d8239b] transition-colors font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                ← Back to Summit Home
              </a>
            </div>
          </div>
        </div>
      </main>

      <ChoosePathModal open={modalOpen} onOpenChange={setModalOpen} />
    </div>
  );
};

export default Day2;
